<script setup>
/**
 * FormControlUrlSlug
 * A URL slug input that can automatically sync with a title field.
 * It formats the input into a URL-friendly slug and can be configured to stop
 * syncing when manually edited.
 */
import {ref, computed, watch, getCurrentInstance, onMounted} from 'vue'
import FormControlTypeText from '../form-control-type/FormControlTypeText.vue'

const {proxy} = getCurrentInstance()
const store = proxy.$store

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
})

const doSync = ref(true)

// --- Computed ---
const tableName = computed(() => store.state[props.formId]?.tableName)
const componentId = computed(() => store.state.components.componentIds[tableName.value])
const nameField = computed(() => store.getters['components/nameField'](componentId.value))
const titleValue = computed(() => nameField.value ? props.currentData[nameField.value] : undefined)

// --- Methods ---
const formatValue = (str) => {
    return str === undefined || str === null
        ? ''
        : String(str)
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, '')      // Remove accents (e.g., é -> e)
            .replace(/[^a-zA-Z0-9/-]+/g, ' ')       // Replace all non-alphanumerics with space
            .toLowerCase()
            .trim()
            .replace(/ +/g, '-')                  // Collapse spaces into dashes
}

// --- Lifecycle ---
onMounted(() => {
    // If the field is already populated then syncing should be disabled.
    if (props.currentData[props.field.name]) {
        doSync.value = false
    }
})

// --- Watchers ---
watch(() => props.currentData[props.field.name], (val) => {
    // If the user manually empties the field, start syncing again.
    if (val === '') {
        doSync.value = true
    }

    // Applies formatting when the user modifies the URL directly.
    const formattedValue = formatValue(val)
    if (formattedValue !== val) {
        props.currentData[props.field.name] = formattedValue
    }
})

watch(titleValue, (val, oldVal) => {
    // If this field's current value is the formatted version of the old title value, then
    // the values are currently in sync, so set it to the formatted version of the new value.
    if (
        doSync.value &&
        (
            props.currentData[props.field.name] === formatValue(oldVal) ||
            props.currentData[props.field.name] === ''
        )
    ) {
        props.currentData[props.field.name] = formatValue(val)
    }
})
</script>

<template>
    <FormControlTypeText
        :form-id="formId"
        :field="field"
        :current-data="currentData"
    />
</template>